import { useEffect, useState, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { useApp } from "../context/AppContext";
import DateRangePicker from "../components/DateRangePicker";
import StatCard from "../components/StatCard";
import { SERVICE_LOCATION, SITE_MEASURES_API, DOWNLOAD_SUBMITTED_WORKSHEET } from "../constants/endpoints";
import { WORKCARDS_DOWNLOADED } from "../constants/errors";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import downloadIcon from "../assets/icons/download.svg";

// ── Helpers ──────────────────────────────────────────────────
const dateOnly = (str) => {
    if (!str) return "";
    const d = str.substring(0, str.indexOf("T"));
    return d === "1900-01-01" ? "" : d;
};

const fmtDate = (str) => {
    if (!str) return "—";
    const [y, m, d] = str.split("-");
    return `${d}/${m}/${y}`;
};

// ── Status badge ─────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const map = {
        COMPLETE: "complete",
        INVOICED: "invoiced",
        NEW: "new",
        "APPLIED FOR": "applied",
        "Site Attention": "attention",
    };
    const cls = map[status] || "new";
    return <span className={`status-badge status-badge--${cls}`}>{status}</span>;
};

// ── Tabs ───────────────────────────────────────────────────
const TAB_COMPLETED = "completed";
const TAB_INVOICED = "invoiced";

// ── Sites Component ────────────────────────────────────────────
const Sites = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { setLoading, showToast } = useApp();

    const { data } = location.state || {};
    const siteInfo = data?.siteInfo || {};

    const [allMeasures, setAllMeasures] = useState([]);
    const [tabMeasures, setTabMeasures] = useState([]);
    const [activeTab, setActiveTab] = useState(TAB_COMPLETED);
    const [selected, setSelected] = useState([]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [downloading, setDownloading] = useState(false);

    const dateRef = useRef({ start: "", end: "" });

    // ── Tab counts ──────────────────────
    const completedCount = allMeasures.filter(m => m.measureStatus === "COMPLETE").length;
    const invoicedCount = allMeasures.filter(m => m.measureStatus === "INVOICED" || m.measureStatus === "APPLIED FOR").length;

    // ── Fetch Measures ────────────────────────────────────────────────────
    const fetchMeasures = useCallback((startDate, endDate) => {
        if (!siteInfo.systemId) return;
        setLoading(true);
        axios
            .post(
                SERVICE_LOCATION + SITE_MEASURES_API,
                { systemId: siteInfo.systemId, startDate, endDate },
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            )
            .then(res => {
                setLoading(false);
                if (res.data.isSuccess) {
                    const raw = res.data.data.getSiteMeasures || [];
                    const cleaned = raw.map(item => ({
                        ...item,
                        calloffRequested: dateOnly(item.calloffRequested),
                        measureCompleted: dateOnly(item.measureCompleted),
                    }));
                    setAllMeasures(cleaned);
                    setSelected([]);
                } else {
                    setAllMeasures([]);
                    setSelected([]);
                    setTabMeasures([]);
                    setActiveTab(TAB_COMPLETED);
                    globalFilter("");
                }
            })
            .catch(() => {
                setLoading(false);
            })
    }, [data, siteInfo.systemId, setLoading, showToast]);

    const handleDateChange = useCallback((start, end) => {
        dateRef.current = { start, end };
        fetchMeasures(start, end);
    }, [fetchMeasures]);

    // ── Filter by active tab ──────────────────────────────────────
    useEffect(() => {
        let filtered = [];
        if (activeTab === TAB_COMPLETED) {
            filtered = allMeasures.filter(m => m.measureStatus === "COMPLETE");
        } else {
            filtered = allMeasures.filter(m =>
                m.measureStatus === "INVOICED" || m.measureStatus === "APPLIED FOR"
            );
        }
        setTabMeasures(filtered);
        setSelected([]);
        setGlobalFilter("");
    }, [activeTab, allMeasures]);

    // ── Clear selection on tab switch ──────────────────────────────────────────────
    const handleTabSwitch = (tab) => {
        setActiveTab(tab);
        setSelected([]);
        setGlobalFilter("");
    };

    // ── back to sites ──────────────────────────────────────────────
    const handleBackToSites = () => {
        navigate("/home/dashboard", { state: { data } });
    }

    // ── PDF Download ──────────────────────────────────────────────
    const handleDownload = () => {
        if (!selected.length) return;
        setDownloading(true);
        setLoading(true);
        const submissions = selected.map(r => ({ submissionId: r.submissionId }));
        axios
            .post(
                SERVICE_LOCATION + DOWNLOAD_SUBMITTED_WORKSHEET,
                { isApproveForm: false, getWorkCardsRequestDTO: submissions },
                { headers: { Authorization: `Bearer ${data.data.token}` } }
            )
            .then(response => {
                setLoading(false);
                setDownloading(false);
                if (response.data.isSuccess) {
                    if (response.data.data.downloadedWorkCards.length > 0) {
                        (response.data.data.downloadedWorkCards).forEach(workcard => {
                            const base64 = workcard.chunks.join("");
                            const base64String = "data:application/pdf;base64," + base64;
                            const linkSource = base64String;
                            const downloadLink = document.createElement("a");
                            const fileName = workcard.fileName;
                            downloadLink.href = linkSource;
                            downloadLink.download = fileName;
                            downloadLink.click();
                        });
                    }
                    showToast("success", WORKCARDS_DOWNLOADED || "Worksheets downloaded.");
                }
                else if (!response.data.isSuccess) {
                    showToast("error", response.data.errorMessage || "Download failed.");
                }
            })
            .catch(() => {
                setLoading(false);
                setDownloading(false);
                showToast("error", "Network error during download.");
            });
    };

    // ── Column templates ──────────────────────────────────────────
    const statusTemplate = (row) => <StatusBadge status={row.measureStatus} />;
    const calloffTemplate = (row) => fmtDate(row.calloffRequested);
    const completedTemplate = (row) => fmtDate(row.measureCompleted);

    // ── Disbale unsubmitted worksheets to prevent from download ────────────────────────────────
    const isSelectable = (data) => data.submissionId !== 0;
    const isRowSelectable = (event) => (event.data ? isSelectable(event.data) : true);
    const rowClassName = (data) => (isSelectable(data) ? "" : "p-disabled");

    // ── DataTable header (toolbar) ────────────────────────────────
    const tableHeader = (
        <div style={styles.toolbar}>
            {/* Left: Date Range Picker */}
            <DateRangePicker onChange={handleDateChange} defaultPreset="allTime" />

            {/* Right: Download & Search */}
            <div style={styles.toolbarRight}>

                {/* Download */}
                <Button
                    label={selected.length > 0 ? `Download (${selected.length})` : "Download PDF"}
                    icon={<img src={downloadIcon} alt="" width={14} height={14} style={{ marginRight: 6 }} />}
                    disabled={selected.length === 0 || downloading}
                    onClick={handleDownload}
                    style={{
                        background: selected.length > 0 ? "#F0FDF4" : "#F9FAFB",
                        color: selected.length > 0 ? "#166534" : "#9CA3AF",
                        border: selected.length > 0 ? "1px solid #4CAF50" : "1px solid #E5E7EB",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        padding: "7px 14px",
                        cursor: selected.length > 0 ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: 4,
                        boxShadow: "none",
                    }}
                />

                {/* Search */}
                <span className="p-input-icon-left" style={{ display: "flex", alignItems: "center" }}>
                    <i className="pi pi-search" style={{ fontSize: 13, color: "#9CA3AF" }} />
                    <InputText
                        value={globalFilter}
                        onChange={e => setGlobalFilter(e.target.value)}
                        placeholder="Search measures…"
                        style={{
                            fontSize: 13,
                            height: 36,
                            paddingLeft: 32,
                            borderRadius: 6,
                            border: "1px solid #D1D5DB",
                            width: 220,
                            fontFamily: "inherit",
                        }}
                    />
                </span>
            </div>
        </div>
    );

    return (
        <div className="page-enter">
            {/* Page header */}
            <div style={styles.pageHeader}>
                <div style={styles.left}>
                    <h1 style={styles.pageTitle}>{siteInfo.siteName}</h1>
                    <span style={styles.siteId}>REF: {siteInfo.siteRefNumber}</span>
                </div>

                <div style={styles.right}>
                    <Button style={styles.linkButton} label="Back to Sites" link onClick={handleBackToSites} />
                </div>
            </div>

            {/* Stat cards */}
            <div style={styles.statsRow}>
                <StatCard icon="pi pi-list" label="Total Measures" value={siteInfo.totalMeasures.toLocaleString()} color="#4CAF50" tooltip="Combined total of all completed and invoiced measures for the site" />
                <StatCard icon="pi pi-building" label="Total Plots" value={siteInfo.totalPlots.toLocaleString()} color="#3B82F6" tooltip="Total plots associated for all completed and invoiced measures for the site" />
            </div>

            {/* Table card */}
            <div className="mx-card" style={{ padding: 0 }}>
                {/* Tabs */}
                <div style={{ padding: "0 20px" }}>
                    <div className="mx-tabs">
                        <button className={`mx-tab${activeTab === TAB_COMPLETED ? " active" : ""}`} onClick={() => handleTabSwitch(TAB_COMPLETED)} >
                            Completed Measures
                            <span style={{ ...styles.tabCount, ...(activeTab === TAB_COMPLETED ? styles.tabCountActive : {}), }}>
                                {completedCount}
                            </span>
                        </button>
                        <button className={`mx-tab${activeTab === TAB_INVOICED ? " active" : ""}`} onClick={() => handleTabSwitch(TAB_INVOICED)} >
                            Invoiced Measures
                            <span style={{ ...styles.tabCount, ...(activeTab === TAB_INVOICED ? styles.tabCountActive : {}), }}>
                                {invoicedCount}
                            </span>
                        </button>
                    </div>
                </div>

                {/* PrimeReact DataTable */}
                <DataTable value={tabMeasures} dataKey="measureId" selectionMode="checkbox" selection={selected} onSelectionChange={e => setSelected(e.value)}
                    globalFilter={globalFilter} globalFilterFields={["measureId", "submissionId", "plotNumber", "plotDescription", "measureDescription", "measureQuantity", "worksheetCode", "locationCode",
                        "totalRevenue", "payyAppNo", "measureStatus", "measureContractNumber", "calloffRequested", "measureCompleted",]}
                    removableSort sortMode="multiple"
                    header={tableHeader} paginator rows={10} rowsPerPageOptions={[10, 20, 50]} emptyMessage="No measures available for the selected period"
                    style={{ fontSize: 13 }} isDataSelectable={isRowSelectable} rowClassName={rowClassName} scrollable >
                    <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
                    <Column field="measureId" header="Measure Id" sortable style={{ minWidth: 120 }} />
                    <Column field="submissionId" header="Submission Id" style={{ minWidth: 120 }} hidden />
                    <Column field="plotNumber" header="Plot Number" sortable style={{ minWidth: 120 }} />
                    <Column field="plotDescription" header="Plot Description" sortable style={{ minWidth: 160 }} />
                    <Column field="measureDescription" header="Measure" sortable style={{ minWidth: 180 }} />
                    <Column field="measureQuantity" header="Qty" sortable style={{ minWidth: 70, textAlign: "right" }} />
                    <Column field="worksheetCode" header="Worksheet" sortable style={{ minWidth: 120, fontFamily: "var(--font-mono)", fontSize: 12 }} />
                    <Column field="locationCode" header="Location" sortable style={{ minWidth: 100 }} />
                    <Column field="totalRevenue" header="Price" sortable style={{ minWidth: 90, textAlign: "right" }} />
                    <Column field="payyAppNo" header="App No" sortable style={{ minWidth: 90 }} />
                    <Column field="measureStatus" header="Status" sortable body={statusTemplate} style={{ minWidth: 120 }} />
                    <Column field="measureContractNumber" header="Order No" sortable style={{ minWidth: 110, fontFamily: "var(--font-mono)", fontSize: 12 }} />
                    <Column field="calloffRequested" header="Calloff Date" sortable body={calloffTemplate} style={{ minWidth: 120 }} />
                    <Column field="measureCompleted" header="Completed" sortable body={completedTemplate} style={{ minWidth: 110 }} />
                </DataTable>

            </div>
        </div>
    );
};

// ── Styles ────────────────────────────────────────────────────
const styles = {
    pageHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: 6,
    },
    pageTitle: {
        fontSize: 22,
        fontWeight: 700,
        color: "#111827",
        marginBottom: 4,
    },
    siteId: {
        fontSize: 13,
        color: "#6B7280",
        fontFamily: "var(--font-mono)",
        display: "block",
        marginBottom: 20,
    },
    statsRow: {
        display: "flex",
        gap: 16,
        marginBottom: 20,
        flexWrap: "wrap",
    },
    tabCount: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        marginLeft: 7,
        minWidth: 20,
        height: 18,
        padding: "0 6px",
        borderRadius: 99,
        fontSize: 11,
        fontWeight: 600,
        background: "#F3F4F6",
        color: "#6B7280",
        lineHeight: 1,
    },
    tabCountActive: {
        background: "#DCFCE7",
        color: "#166534",
    },
    toolbar: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        padding: "14px 0 14px 0",
        flexWrap: "wrap",
        gap: 12,
    },
    toolbarRight: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexWrap: "wrap",
    },

    /* ── Left ── */
    left: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 2,
        cursor: "pointer",
        userSelect: "none",
        textDecoration: "none",
    },

    /* ── Right ── */
    right: {
        display: "flex",
        alignItems: "flex-end",
        gap: 6,
        marginTop: -50,
    },
    linkButton: {
        color: "#4CAF50",
    }
};

export default Sites;